/* ============================================================================
   tpw-cart-roll.js — roll the header cart numerals on every cart change (F-07)
   ----------------------------------------------------------------------------
   The header cart shows TWO live numerals:
     · #cart-icon-bubble .header-cart__bubble-count   the item count ("11")
     · #cart-icon-bubble .header-cart__total          the $ total ("$804.80")
   Both are the innerHTML of #cart-icon-bubble, which EVERY add/remove/update
   path (cart drawer cart.js, product-form.js, pdpv9.js → renderContents) swaps
   wholesale via the cart-icon-bubble section re-render.

   Rather than hook each path (their publish/render ORDER differs — product-form
   publishes cartUpdate BEFORE renderContents, cart-items AFTER), we watch the
   bubble element directly with a MutationObserver. On any change we read the new
   count + total, diff against the cached previous values, and hand them to the
   shared roller (window.TPWRoll). Path-agnostic, timing-safe, works identically
   in the full and condensed header states. prefers-reduced-motion -> TPWRoll
   does an instant swap.

   ⚠ SELF-MUTATION GUARD: our own roll writes (innerHTML of the count/total spans)
   live INSIDE the observed subtree, so they would re-trigger the observer and
   loop forever (this froze the renderer in QA). We set `selfWriting` around every
   roll write and the observer ignores mutations while it's true. We only react to
   EXTERNAL replacements (the theme's renderContents swapping the whole bubble).

   Zero-CLS: the roll only translates inner cells; the bubble box is unchanged.
   The .visually-hidden sr-mirrors next to each numeral are left untouched, so
   assistive tech still reads the real value (we only touch the aria-hidden
   visual span).
   ============================================================================ */
(function () {
  'use strict';

  var BUBBLE_ID = 'cart-icon-bubble';
  var COUNT_SEL = '.header-cart__bubble-count';
  var TOTAL_SEL = '.header-cart__total';

  var prevCount = null;   // last seen count text
  var prevTotal = null;   // last seen total text
  var first = true;       // first observation = baseline (no roll on page load)
  var obs = null;         // the MutationObserver (disconnected during our writes)
  var watchEl = null;     // STABLE ancestor we observe (survives #cart-icon-bubble being replaced)

  // Read a numeral's TRUE value. The theme renders fresh plain-text count/total
  // nodes on each cart update, but our own roll reel-shapes them in place. A
  // reel host's textContent is the concatenated spin sequence (e.g. "$54987.76…"),
  // NOT its resting value — so if the observer re-fires for an unrelated mutation
  // in the watched section after we've rolled, a naive textContent read would feed
  // that garbage back as the "new value" and roll TO it (header total corruption,
  // 2026-06-26). When the node is already one of our reel hosts, trust the value
  // we committed to data-tpw-roll-text instead; only plain nodes use textContent.
  function txt(node) {
    if (!node) return null;
    if (node.classList && node.classList.contains('tpw-roll')) {
      var v = node.getAttribute('data-tpw-roll-text');
      if (v != null) return v.trim();
    }
    return node.textContent.trim();
  }

  function apply(node, value, prev) {
    if (!node || value == null || !window.TPWRoll) return;
    if (prev == null || first || value === prev) {
      // first appearance / page-load baseline / unchanged-but-re-rendered:
      // shape as reels WITHOUT animating.
      window.TPWRoll.set(node, value);
    } else {
      // changed: roll from the cached previous value (the fresh node has no
      // per-element memory because the bubble was re-rendered wholesale).
      window.TPWRoll.roll(node, value, { from: prev });
    }
  }

  function syncFromBubble(bubble) {
    if (!bubble) return;
    var countEl = bubble.querySelector(COUNT_SEL);
    var totalEl = bubble.querySelector(TOTAL_SEL);
    var newCount = txt(countEl);
    var newTotal = txt(totalEl);

    // Guard: our roll/set writes mutate INSIDE the observed subtree, which would
    // re-trigger the observer and loop forever. Disconnect around our writes,
    // then take the observer's own records (so the suppressed self-mutations are
    // discarded, not queued) and reconnect. Bulletproof vs any rAF race.
    if (obs) obs.disconnect();
    apply(countEl, newCount, prevCount);
    apply(totalEl, newTotal, prevTotal);
    if (obs) { obs.takeRecords(); obs.observe(watchEl || bubble, OBS_OPTS); }

    // Keep the last known value when the cart empties — at 0 items the theme
    // removes the count/total elements entirely (cart-count-bubble.liquid), so
    // newCount/newTotal come back null. Without this guard prev would be wiped to
    // null and the next add would snap instead of roll. Retaining the last value
    // means the first add after emptying still rolls from it.
    if (newCount != null) prevCount = newCount;
    if (newTotal != null) prevTotal = newTotal;
    first = false;
  }

  var OBS_OPTS = { childList: true, subtree: true, characterData: true };

  function init() {
    var bubble = document.getElementById(BUBBLE_ID);
    if (!bubble) return;

    // Observe a STABLE ANCESTOR, not #cart-icon-bubble itself. The add/update
    // paths (and an on-load cart sync) re-render the cart-icon-bubble SECTION,
    // which REPLACES the #cart-icon-bubble element wholesale — an observer bound
    // to the old element goes deaf and the count roll silently dies (this is why
    // it "dropped"). The .shopify-section wrapper persists across those swaps, so
    // we watch it (subtree) and re-query the live count/total each time.
    watchEl = bubble.closest('.shopify-section') || bubble.parentElement || bubble;

    // Baseline shape on load (no animation), so the first real change rolls
    // cleanly from the rendered value.
    syncFromBubble(bubble);

    // Observe the bubble's subtree: renderContents replaces its innerHTML.
    // Debounce with setTimeout(0) (NOT rAF) — rAF can be starved on this theme
    // (footer map blocks document_idle; background tabs never tick), which would
    // stall the reaction. setTimeout always fires, so the roll is reliable and
    // the value never gets stuck unshaped.
    var pending = false;
    obs = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        syncFromBubble(document.getElementById(BUBBLE_ID));
      }, 0);
    });
    obs.observe(watchEl, OBS_OPTS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ==========================================================================
     P4 (founder 2026-06-26): roll the DRAWER line totals + subtotal too.
     ----------------------------------------------------------------------------
     The header count/total already roll via the MutationObserver above. The
     drawer's line prices + subtotal live inside #CartDrawer, which cart.js
     re-renders WHOLESALE on every qty change / remove — so a per-element memory
     can't survive. cart.js calls these two globals around its section swap:

       var snap = window.tpwCartRollSnapshot();   // BEFORE the innerHTML swap
       ... swap sections ...
       window.tpwCartRollApply(snap);             // AFTER — rolls new from old

     We key each line by its cart-item id (#CartItem-N / #CartDrawer-Item-N),
     which Shopify reindexes consistently, so a line that stayed put rolls from
     its own previous price; a removed line simply has no new node to roll.
     Subtotal is matched 1:1. TPWRoll commits the true value up front + honours
     reduced-motion (instant), so a dropped frame never leaves a stale price and
     the box never resizes (zero CLS).
     ========================================================================== */
  var LINE_PRICE_SEL = '.cart-item__final-price';
  var SUBTOTAL_SEL = '.totals__subtotal-value > .h5, .totals__subtotal-value span.h5';

  // Identify a line price node by its owning cart-item's id (stable across
  // reindex for lines that didn't move; null = can't key it, skip the roll).
  function lineKey(priceEl) {
    var item = priceEl.closest('.cart-item');
    return item && item.id ? item.id : null;
  }

  function snapshot() {
    var map = { lines: {}, subtotal: null };
    document.querySelectorAll(LINE_PRICE_SEL).forEach(function (el) {
      var k = lineKey(el);
      if (k) map.lines[k] = el.textContent.trim();
    });
    var sub = document.querySelector(SUBTOTAL_SEL);
    if (sub) map.subtotal = sub.textContent.trim();
    return map;
  }

  // Clean text content of a node, ignoring any reel markup we may have injected
  // on a PRIOR roll (a reel-ified node's textContent is the full spin sequence,
  // e.g. "4321098..." — never read THAT as the target). If the node is already a
  // roll host, reconstruct the resting value from each reel's first (target) cell
  // + the lit/static glyphs; otherwise it's plain text.
  function cleanText(el) {
    if (!el) return null;
    if (!el.classList || !el.classList.contains('tpw-roll')) {
      return el.textContent.trim();
    }
    var out = '';
    var kids = el.childNodes;
    for (var i = 0; i < kids.length; i++) {
      var n = kids[i];
      if (n.nodeType === 3) {
        out += n.textContent;
      } else if (n.classList && n.classList.contains('tpw-roll__reel')) {
        var cell = n.querySelector('.tpw-roll__cell'); // cell 0 = resting target
        out += cell ? cell.textContent : '';
      } else if (n.classList && n.classList.contains('tpw-roll__lit')) {
        out += n.textContent;
      } else {
        out += n.textContent;
      }
    }
    return out.trim();
  }

  // DRAWER line price + subtotal use Option B "count tween" (founder 2026-06-27):
  // a smooth count old->new, not the per-digit reel (which read "busy" here). The
  // HEADER count/total keep the reel roll via apply() above. Plain text (no reels).
  function rollNode(el, from, to) {
    if (!el || to == null) return;
    if (from == null || from === to || !window.TPWRoll || !window.TPWRoll.tween) {
      el.textContent = to; // unchanged / no prior value / no lib: just set it
    } else {
      window.TPWRoll.tween(el, from, to);
    }
  }

  // NB: named applySnapshot, NOT apply — a sibling apply() (the header
  // count/total roller, line ~45) lives in this same IIFE scope. Two function
  // declarations with the same name hoist and the LATTER wins, so naming this
  // `apply` silently shadowed the header roller across the whole file and the
  // header count/total stopped reel-shaping ("roll dropped" bug, 2026-06-26).
  function applySnapshot(snap) {
    if (!snap) return;
    var lines = snap.lines || {};
    var subtotal = snap.subtotal;
    // Capture the NEW (clean, freshly-swapped) target values SYNCHRONOUSLY now,
    // before any roll reel-ifies these nodes — reading el.textContent AFTER a roll
    // would return the concatenated spin sequence (garbage). We pass the captured
    // clean target into rollNode rather than re-reading the live node.
    var lineTargets = [];
    document.querySelectorAll(LINE_PRICE_SEL).forEach(function (el) {
      lineTargets.push({ el: el, key: lineKey(el), to: cleanText(el) });
    });
    var sub = document.querySelector(SUBTOTAL_SEL);
    var subTo = sub ? cleanText(sub) : null;

    // Defer one tick so layout is settled, then roll from old -> captured new.
    setTimeout(function () {
      lineTargets.forEach(function (t) {
        rollNode(t.el, t.key ? lines[t.key] : null, t.to);
      });
      if (sub) rollNode(sub, subtotal, subTo);
    }, 0);
  }

  window.tpwCartRollSnapshot = snapshot;
  window.tpwCartRollApply = applySnapshot;
})();
